import router, { useRouter } from 'next/router';

import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { useNProgress } from '@tanem/react-nprogress';

import { darkThemePaths } from 'layout/utils/darkThemePaths';
import { useIsMounted } from 'shared/hooks/useIsMounted';
import { useStoreSelector } from 'store/hooks/storeHooks';

export const ProgressBar = () => {
  const [isFetching, setIsFetching] = useState(false);
  const { isMenuVisible } = useStoreSelector((state) => state.pageLayout);
  const { pathname, asPath } = useRouter();
  const isMounted = useIsMounted();
  const { animationDuration, isFinished, progress } = useNProgress({
    isAnimating: isFetching,
  });

  const invertLoaderColor = isMenuVisible && !darkThemePaths?.includes(pathname);

  useEffect(() => {
    const handleRouteChangeStart = () => {
      if (asPath.includes('pageNumber')) {
        return;
      }
      if (isMounted()) {
        setIsFetching(true);
      }
    };
    const handleRouteChangeComplete = () => {
      if (isMounted()) {
        setIsFetching(false);
      }
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [asPath]);

  return (
    <ProgressBar.Container isFinished={isFinished} animationDuration={animationDuration}>
      <ProgressBar.BarWrapper
        $invertLoaderColor={invertLoaderColor}
        progress={progress}
        animationDuration={animationDuration}
      />
    </ProgressBar.Container>
  );
};

ProgressBar.Container = styled.div<{ animationDuration: number; isFinished: boolean }>`
  opacity: ${({ isFinished }) => (isFinished ? 0 : 1)};
  pointer-events: none;
  transition: ${({ animationDuration }) => `opacity ${animationDuration}ms linear`};
  position: fixed;
  top: 0;
  right: 0;
  z-index: ${({ theme }) => theme.zIndex.pageLoader};
  width: 100%;
`;

ProgressBar.BarWrapper = styled.div<{
  animationDuration: number;
  $invertLoaderColor: boolean;
  progress: number;
}>`
  background: ${({ theme }) => theme.navigation.loaderColor};
  height: 2px;
  left: 0;
  margin-left: ${({ progress }) => `${(-1 + progress) * 100}%`};
  transition: ${({ animationDuration }) =>
    animationDuration && `margin-left ${animationDuration}ms linear`};
  width: 100%;
  ${({ $invertLoaderColor }) =>
    $invertLoaderColor &&
    `-webkit-filter: invert(100%);
    filter: invert(100%);
    `}
`;

export default ProgressBar;
